import { sva } from "@stubhub/broadway-foundation/css";

export const theaterPromoBanner = sva({
  className: "theaterPromoBanner",
  slots: [
    "root",
    "hero",
    "heroImage",
    "heroImageAnimated",
    "heroScrim",
    "heroFooter",
    "heroContent",
    "title",
    "subtitle",
    "ctaPill",
    "ctaPillText",
    "ctaPillArrow",
    "ctaCard",
    "ctaCopy",
    "ctaKicker",
    "ctaPrice",
    "ctaButton",
  ],
  base: {
    root: {
      position: "relative",
      width: "full",
    },
    hero: {
      position: "relative",
      width: "full",
      // The image always shows 282px; the box is 282 + the body card's 24px lift = 306px.
      // Width is 100% of the viewport, so the visible crop widens with the screen — the artwork
      // must keep the important content in the central area.
      height: "[306px]",
      overflowX: "hidden",
      overflowY: "hidden",
      tablet: {
        height: "[424px]",
      },
    },
    heroImage: {
      position: "absolute",
      insetBlockStart: "primitives.0",
      insetInlineStart: "primitives.0",
      width: "full",
      height: "full",
      objectFit: "cover",
    },
    // The 2b hero is a still raster; the Lottie's only animation was a 2.5s scale push.
    heroImageAnimated: {
      animationName: "[theaterPromoPush]",
      animationDuration: "[2.5s]",
      animationTimingFunction: "[linear]",
      animationFillMode: "[forwards]",
      _motionReduce: {
        animationName: "[none]",
      },
    },
    heroScrim: {
      position: "absolute",
      insetBlockStart: "primitives.0",
      insetInlineEnd: "primitives.0",
      insetBlockEnd: "primitives.0",
      insetInlineStart: "primitives.0",
      backgroundImage:
        "[linear-gradient(0deg, rgba(0,0,0,0.70) 24%, rgba(0,0,0,0.10) 43.8%, rgba(0,0,0,0.24) 76%, rgba(0,0,0,0.56) 100%)]",
    },
    heroFooter: {
      position: "absolute",
      insetInlineStart: "primitives.0",
      insetInlineEnd: "primitives.0",
      // Figma stacks the title and the "Featured:" line higher on phone than on tablet.
      insetBlockEnd: "[38px]",
      display: "flex",
      flexDirection: "row",
      alignItems: "flex-end",
      columnGap: "primitives.100",
      boxSizing: "content-box",
      maxWidth: "contentMaxWidth",
      marginInlineStart: "auto",
      marginInlineEnd: "auto",
      paddingInlineStart: "primitives.200",
      paddingInlineEnd: "primitives.200",
      tablet: {
        insetBlockEnd: "primitives.500",
        paddingInlineStart: "primitives.400",
        paddingInlineEnd: "primitives.400",
      },
    },
    heroContent: {
      display: "flex",
      flexDirection: "column",
      // 8px between the category title and the "Featured:" line, on every breakpoint.
      rowGap: "primitives.100",
      flexGrow: "[1]",
      flexBasis: "[0]",
      minWidth: "primitives.0",
    },
    // Figma: Heading variant="header" size="medium" — 20/24, tracking normal.
    title: {
      color: "text.onFill",
      fontSize: "primitives.500",
      fontWeight: "bold",
      letterSpacing: "primitives.normal",
      lineHeight: "primitives.600",
      tablet: {
        fontSize: "primitives.1200",
        lineHeight: "primitives.1300",
      },
    },
    // Figma: Text variant="body" size="medium". The CMS label carries no emphasis runs.
    subtitle: {
      textStyle: "body.medium",
      color: "text.onFill",
    },
    // v1 phone CTA: a liquid-glass pill over the hero (Figma node 9434-70185). Phone only — from
    // tablet up the plain `subtitle` line and the desktop `ctaCard` are the CTA.
    ctaPill: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      columnGap: "primitives.100",
      alignSelf: "flex-start",
      minHeight: "primitives.400",
      paddingBlockStart: "primitives.75",
      paddingBlockEnd: "primitives.75",
      paddingInlineStart: "primitives.150",
      paddingInlineEnd: "primitives.150",
      boxSizing: "border-box",
      overflowX: "hidden",
      overflowY: "hidden",
      backgroundColor: "[rgba(255,255,255,0.12)]",
      borderBlockWidth: "primitives.25",
      borderInlineWidth: "primitives.25",
      borderBlockStyle: "solid",
      borderInlineStyle: "solid",
      borderBlockColor: "[rgba(255,255,255,0.2)]",
      borderInlineColor: "[rgba(255,255,255,0.2)]",
      borderStartStartRadius: "element.small",
      borderStartEndRadius: "element.small",
      borderEndEndRadius: "element.small",
      borderEndStartRadius: "element.small",
      backdropFilter: "[blur(9px)]",
      textDecorationLine: "none",
      tablet: { display: "none" },
    },
    // Figma: Text variant="label" size="small" colour="text.onFill".
    ctaPillText: {
      textStyle: "label.small",
      color: "text.onFill",
    },
    ctaPillArrow: {
      display: "flex",
      alignItems: "center",
      flexShrink: "[0]",
      color: "text.onFill",
    },
    ctaCard: {
      display: "none",
      tablet: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        flexShrink: "[0]",
        // minWidth, not width: the copy and the button keep their content width (the kicker is
        // nowrap), so the card grows rather than wrapping or overflowing.
        minWidth: "[340px]",
        // Room between the "See …" copy and the "Find tickets" button.
        columnGap: "primitives.150",
        paddingBlockStart: "[15px]",
        paddingBlockEnd: "[15px]",
        paddingInlineStart: "[19px]",
        paddingInlineEnd: "[19px]",
        backgroundColor: "[rgba(255,255,255,0.12)]",
        borderBlockWidth: "primitives.25",
        borderInlineWidth: "primitives.25",
        borderBlockStyle: "solid",
        borderInlineStyle: "solid",
        borderBlockColor: "[rgba(255,255,255,0.2)]",
        borderInlineColor: "[rgba(255,255,255,0.2)]",
        borderStartStartRadius: "primitives.200",
        borderStartEndRadius: "primitives.200",
        borderEndEndRadius: "primitives.200",
        borderEndStartRadius: "primitives.200",
        backdropFilter: "[blur(9px)]",
        textDecorationLine: "none",
      },
    },
    ctaCopy: {
      display: "flex",
      flexDirection: "column",
    },
    ctaKicker: {
      color: "text.onFill",
      fontSize: "primitives.350",
      fontWeight: "medium",
      lineHeight: "primitives.500",
      // "See The Book of Mormon in NYC" must stay on one line; the card grows to fit it.
      whiteSpace: "nowrap",
    },
    ctaPrice: {
      color: "text.onFill",
      fontSize: "primitives.600",
      fontWeight: "bold",
      lineHeight: "primitives.700",
    },
    ctaButton: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "[#1a2329]",
      color: "text.onFill",
      fontSize: "primitives.350",
      fontWeight: "medium",
      lineHeight: "primitives.500",
      minHeight: "primitives.600",
      paddingBlockStart: "primitives.175",
      paddingBlockEnd: "primitives.175",
      paddingInlineStart: "primitives.200",
      paddingInlineEnd: "primitives.200",
      borderStartStartRadius: "primitives.150",
      borderStartEndRadius: "primitives.150",
      borderEndEndRadius: "primitives.150",
      borderEndStartRadius: "primitives.150",
      whiteSpace: "nowrap",
    },
  },
  variants: {
    // v1: the phone CTA is the pill, so the plain "Featured: …" line hides on phone. It returns
    // from tablet up, where the desktop `ctaCard` is the CTA.
    cta: {
      inline: {},
      pill: {
        subtitle: {
          display: "none",
          tablet: { display: "block" },
        },
      },
    },
  },
  defaultVariants: {
    cta: "inline",
  },
});
